import api from './api';
import SparkMD5 from 'spark-md5';

class ChunkedUploadService {
    constructor() {
        this.DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
        this.MAX_CONCURRENT_CHUNKS = 3;
        this.MAX_RETRIES = 3;
        this.RETRY_DELAY = 1000; // 1 second
    }

    /**
     * Upload a file using direct S3 multipart upload
     */
    async uploadFile(file, platform, options = {}) {
        const {
            parentAppId = null,
            chunkSize = this.DEFAULT_CHUNK_SIZE,
            onProgress = () => { },
            onChunkComplete = () => { },
            onError = () => { },
            signal = null // AbortController signal
        } = options;

        try {
            // Initialize upload
            const initData = await this.initializeUpload(file, platform, parentAppId, chunkSize);
            const { uploadId, key, totalChunks } = initData;

            // Array to collect S3 part information
            const completedParts = [];

            // Upload chunks directly to S3
            await this.uploadChunks(file, uploadId, key, totalChunks, {
                chunkSize,
                onProgress,
                onChunkComplete: (chunkIndex, etag, progress) => {
                    completedParts.push({
                        PartNumber: chunkIndex + 1,
                        ETag: etag
                    });
                    onChunkComplete(chunkIndex, progress);
                },
                signal
            });

            // Complete upload
            const result = await this.completeUpload(uploadId, key, completedParts, file.size);

            return result;
        } catch (error) {
            onError(error);
            throw error;
        }
    }

    /**
     * Initialize S3 multipart upload
     */
    async initializeUpload(file, platform, parentAppId = null, chunkSize = this.DEFAULT_CHUNK_SIZE) {
        const response = await api.post('/apps/upload/s3/init', {
            filename: file.name,
            fileSize: file.size,
            platform,
            chunkSize,
            parentAppId
        });

        return response.data;
    }

    /**
     * Upload all chunks with concurrency control
     */
    async uploadChunks(file, uploadId, key, totalChunks, options = {}) {
        const {
            chunkSize = this.DEFAULT_CHUNK_SIZE,
            onProgress = () => { },
            onChunkComplete = () => { },
            signal = null
        } = options;

        const uploadedChunks = new Set();
        let uploadedBytes = 0;
        const startTime = Date.now();
        const errors = [];

        // Create chunk upload tasks
        const chunkTasks = [];
        for (let i = 0; i < totalChunks; i++) {
            chunkTasks.push({ index: i, retries: 0 });
        }

        // Upload chunks with concurrency limit
        let hasError = false;

        const uploadQueue = async () => {
            while (true) {
                // Stop if there's been a fatal error
                if (hasError) break;

                // Check if aborted
                if (signal?.aborted) {
                    hasError = true;
                    throw new Error('Upload aborted');
                }

                // Get next chunk (atomic operation)
                const chunkTask = chunkTasks.shift();
                if (!chunkTask) break;

                const { index: chunkIndex, retries } = chunkTask;

                try {
                    // Extract chunk from file
                    const start = chunkIndex * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const chunk = file.slice(start, end);

                    // Get signed URL for this part
                    const signRes = await api.post('/apps/upload/s3/sign-part', {
                        key,
                        uploadId,
                        partNumber: chunkIndex + 1
                    });
                    const { url } = signRes.data;

                    // Upload chunk directly to S3
                    const response = await fetch(url, {
                        method: 'PUT',
                        body: chunk,
                        headers: {
                            'Content-Type': 'application/octet-stream'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`S3 upload failed with status ${response.status}`);
                    }

                    const etag = response.headers.get('ETag')?.replace(/"/g, '');
                    if (!etag) {
                        throw new Error('S3 upload response missing ETag');
                    }

                    // Mark as uploaded
                    uploadedChunks.add(chunkIndex);
                    uploadedBytes += (end - start);

                    // Calculate progress
                    const progress = {
                        uploadedChunks: uploadedChunks.size,
                        totalChunks,
                        uploadedBytes,
                        totalBytes: file.size,
                        percentage: Math.round((uploadedBytes / file.size) * 100),
                        speed: this.calculateSpeed(uploadedBytes, startTime),
                        eta: this.calculateETA(uploadedBytes, file.size, startTime)
                    };

                    onProgress(progress);
                    onChunkComplete(chunkIndex, etag, progress);
                } catch (error) {
                    console.error(`[Upload] Chunk ${chunkIndex} failed (retry ${retries}/${this.MAX_RETRIES}):`, error.message);

                    // Retry logic: put chunk back in queue if retries left
                    if (retries < this.MAX_RETRIES) {
                        console.warn(`[Upload] Re-queuing chunk ${chunkIndex} for retry ${retries + 1}`);
                        chunkTasks.push({ index: chunkIndex, retries: retries + 1 });
                    } else {
                        console.error(`[Upload] Chunk ${chunkIndex} failed permanently after ${this.MAX_RETRIES} retries`);
                        errors.push({ chunkIndex, error: error.message });
                        hasError = true;
                    }
                }
            }
        };

        // Run concurrent uploads
        const workers = [];
        for (let i = 0; i < this.MAX_CONCURRENT_CHUNKS; i++) {
            workers.push(uploadQueue());
        }

        await Promise.all(workers);

        // Check if all chunks were uploaded
        if (errors.length > 0) {
            throw new Error(`Failed to upload ${errors.length} chunk(s): ${errors.map(e => `#${e.chunkIndex}`).join(', ')}`);
        }

        if (uploadedChunks.size !== totalChunks) {
            const missing = [];
            for (let i = 0; i < totalChunks; i++) {
                if (!uploadedChunks.has(i)) {
                    missing.push(i);
                }
            }
            console.error(`[Upload] Missing chunks:`, missing);
            throw new Error(`Missing chunks: ${uploadedChunks.size}/${totalChunks} uploaded. Missing: ${missing.join(', ')}`);
        }
    }

    /**
     * Complete the upload on the backend
     */
    async completeUpload(uploadId, key, parts, fileSize) {
        const response = await api.post('/apps/upload/s3/complete', {
            uploadId,
            key,
            parts,
            fileSize
        });

        return response.data;
    }

    /**
     * Get upload status (for resumption)
     */
    async getUploadStatus(uploadId) {
        const response = await api.get(`/apps/upload/status/${uploadId}`);
        return response.data;
    }

    /**
     * Abort upload
     */
    async abortUpload(uploadId) {
        const response = await api.delete(`/apps/upload/abort/${uploadId}`);
        return response.data;
    }

    /**
     * Calculate MD5 checksum of file or chunk
     */
    async calculateChecksum(blob) {
        return new Promise((resolve, reject) => {
            const spark = new SparkMD5.ArrayBuffer();
            const fileReader = new FileReader();

            fileReader.onload = (e) => {
                spark.append(e.target.result);
                resolve(spark.end());
            };

            fileReader.onerror = (error) => {
                reject(error);
            };

            fileReader.readAsArrayBuffer(blob);
        });
    }

    /**
     * Calculate upload speed in bytes per second
     */
    calculateSpeed(uploadedBytes, startTime) {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        return elapsedSeconds > 0 ? uploadedBytes / elapsedSeconds : 0;
    }

    /**
     * Calculate estimated time remaining in seconds
     */
    calculateETA(uploadedBytes, totalBytes, startTime) {
        const speed = this.calculateSpeed(uploadedBytes, startTime);
        const remainingBytes = totalBytes - uploadedBytes;
        return speed > 0 ? remainingBytes / speed : 0;
    }

    /**
     * Format bytes to human-readable string
     */
    formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    /**
     * Format seconds to human-readable time
     */
    formatTime(seconds) {
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.round(seconds % 60);
            return `${minutes}m ${secs}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    }
}

export default new ChunkedUploadService();
